import * as THREE from 'three';
import { World } from './world';
import { Player } from './player';
import { Physics } from './physics';
import { AudioManagerInstance } from './audioManager';
import { settingsStore } from './settingsStore';
import { isMobileDevice } from './device';

const isMobile = isMobileDevice();
const PLAYER_HEIGHT = 1.75;

const findSafeMainWorldSpawnY = (world, spawnX, spawnZ) => {
    const offsets = [
        [0, 0], [2, 0], [-2, 0], [0, 2], [0, -2],
        [4, 0], [-4, 0], [0, 4], [0, -4], [3, 3], [-3, 3], [3, -3], [-3, -3],
    ];
    const minY = 0;
    const maxY = world?.chunkSize?.height ?? 32;

    for (const [dx, dz] of offsets) {
        const x = Math.floor(spawnX + dx);
        const z = Math.floor(spawnZ + dz);

        for (let y = maxY; y >= minY; y--) {
            const ground = world.getBlock(x, y, z)?.id ?? 0;
            if (ground === 0) continue;

            const head1 = world.getBlock(x, y + 1, z)?.id ?? 0;
            const head2 = world.getBlock(x, y + 2, z)?.id ?? 0;
            if (head1 === 0 && head2 === 0) {
                return y + 0.5 + PLAYER_HEIGHT + 0.05;
            }
        }
    }

    return 20;
};

export const initGame = (container, userId) => {
    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x87ceeb); // Sky blue
    scene.fog = new THREE.Fog(0x87ceeb, 0, 750);

    // Renderer setup
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const renderer = new THREE.WebGLRenderer({
        antialias: !isMobile,
        powerPreference: "high-performance",
        precision: (isMobile && !isIOS) ? "mediump" : "highp"
    });
    const getRenderSize = () => ({
        width: Math.max(1, container?.clientWidth || window.innerWidth),
        height: Math.max(1, container?.clientHeight || window.innerHeight)
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2));
    {
        const { width, height } = getRenderSize();
        renderer.setSize(width, height);
    }
    renderer.shadowMap.enabled = !isMobile;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(renderer.domElement);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(50, 200, 100);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.width = 2048;
    dirLight.shadow.mapSize.height = 2048;
    scene.add(dirLight);

    // World setup
    const world = new World();
    world.generate();
    scene.add(world);

    // Player setup
    const player = new Player(scene, world, userId);
    const spawnX = 32;
    const spawnZ = 32;
    player.position.set(spawnX, findSafeMainWorldSpawnY(world, spawnX, spawnZ), spawnZ);
    player.velocity.set(0, 0, 0);
    const physics = new Physics(scene);

    // Audio setup
    const audioManager = AudioManagerInstance;
    audioManager.initListener(player.camera);
    audioManager.loadSounds([
        { name: 'block_break', filePath: '/sounds/block_break.wav' },
        { name: 'block_place', filePath: '/sounds/block_place.wav' },
        { name: 'background_music', filePath: '/sounds/background_music.mp3' }
    ]).then(() => {
        audioManager.playSound('background_music', 0.5, true); // Uncomment to play music by default
        console.log("Sounds loaded");
    });

    // Initialize audio settings from settingsStore
    settingsStore.load(userId);
    audioManager.initSettings(settingsStore);

    // Animation Loop
    let animationId;
    let previousTime = performance.now();

    const animate = () => {
        animationId = requestAnimationFrame(animate);

        try {
            if (!renderer || !scene || !player || !player.camera) return;

            const currentTime = performance.now();
            const dt = Math.min((currentTime - previousTime) / 1000, 0.1); // Cap dt to 0.1s to prevent explosions
            previousTime = currentTime;

            // Update player
            if (physics) physics.update(dt, player, world);
            player.update(world);

            // Update world chunks
            if (world) world.update(player);

            // Render
            renderer.render(scene, player.camera);
        } catch (e) {
            console.error("Error in animation loop:", e);
            cancelAnimationFrame(animationId);
        }
    };

    animate();

    // Resize handler
    const onWindowResize = () => {
        const { width, height } = getRenderSize();
        player.camera.aspect = width / height;
        player.camera.updateProjectionMatrix();
        renderer.setSize(width, height);
    };
    window.addEventListener('resize', onWindowResize);

    // Context loss handling
    const onContextLost = (event) => {
        event.preventDefault();
        console.warn('WebGL Context Lost');
    };

    const onContextRestored = () => {
        console.log('WebGL Context Restored. Re-initializing world...');
        // In a complex game, we'd need to re-upload textures and buffers.
        // For this simple version, we can try to trigger a world refresh or let the player know.
        if (world) {
            world.generate(false); // Regenerate without clearing data
        }
    };

    renderer.domElement.addEventListener('webglcontextlost', onContextLost, false);
    renderer.domElement.addEventListener('webglcontextrestored', onContextRestored, false);

    // Lifecycle handling
    const onVisibilityChange = () => {
        if (!document.hidden) {
            onWindowResize();
        }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Cleanup function
    const cleanup = () => {
        cancelAnimationFrame(animationId);
        window.removeEventListener('resize', onWindowResize);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        renderer.domElement.removeEventListener('webglcontextlost', onContextLost);
        renderer.domElement.removeEventListener('webglcontextrestored', onContextRestored);

        // Dispose renderer
        if (renderer.domElement && container.contains(renderer.domElement)) {
            container.removeChild(renderer.domElement);
        }
        renderer.dispose();

        // Dispose world
        world.dispose();

        // Dispose audio
        audioManager.dispose();
    };

    return { cleanup, player, world, resize: onWindowResize };
};
